import { apiFetch } from "./apiClient";
import {
  DEFAULT_ANALYSIS_MODEL,
  DEFAULT_VARIANT_MODEL,
  normalizeModelSelection,
} from "./modelCatalog";

const API_KEY_FIELDS = ["openai", "gemini", "claude", "huggingface", "tribe_server_url", "tribe_api_key", "atlascloud", "kling"];

const emptyApiKeys = () =>
  API_KEY_FIELDS.reduce((acc, field) => {
    acc[field] = "";
    return acc;
  }, {});

const emptyApiKeyStatus = () =>
  API_KEY_FIELDS.reduce((acc, field) => {
    acc[field] = false;
    return acc;
  }, {});

export const defaultUserSettings = {
  uiDarkMode: false,
  generationQcLevel: "basic",
  variantGridColumns: 3,
  analysisModel: DEFAULT_ANALYSIS_MODEL,
  postAnalysisModel: "",
  blogModel: "",
  variantModel: DEFAULT_VARIANT_MODEL,
  generationAspectRatio: "1:1",
  generationTagIds: [],
  knowledgeActiveIds: [],
  generationSourceMode: "knowledge",
  generationSceneMode: "ad",
  specialInstructions: "",
  specialInstructionMode: "rephrase",
  specialInstructionNote: "",
  adCopyCreatives: [],
  atlascloudKey: "",
  apiKeys: emptyApiKeys(),
  apiKeyStatus: emptyApiKeyStatus(),
  modelStats: {},
  activityPreferences: [],
  notifications: {
    completionAudio: true,
    completionPush: false,
    pushPermissionAsked: false,
  },
  // Prompt persistence — synced to DB
  promptLibrary: {},     // Prompt Library block overrides (key → text)
  promptOverrides: {},   // Per-section generation prompt overrides (sectionKey → {main: ""})
  // Integrations
  googleDriveConnected: false,
};

const mergeUserSettings = (data = {}) => ({
  ...defaultUserSettings,
  ...(data || {}),
  analysisModel: normalizeModelSelection(
    data?.analysisModel,
    defaultUserSettings.analysisModel,
  ),
  postAnalysisModel: normalizeModelSelection(
    data?.postAnalysisModel,
    "",
  ),
  blogModel: normalizeModelSelection(
    data?.blogModel,
    "",
  ),
  variantModel: normalizeModelSelection(
    data?.variantModel,
    defaultUserSettings.variantModel,
  ),
  generationSceneMode: ["ad", "product", "review"].includes(
    String(data?.generationSceneMode || "").trim(),
  )
    ? String(data?.generationSceneMode).trim()
    : defaultUserSettings.generationSceneMode,
  apiKeys: {
    ...defaultUserSettings.apiKeys,
    ...((data && data.apiKeys) || {}),
  },
  apiKeyStatus: {
    ...defaultUserSettings.apiKeyStatus,
    ...((data && data.apiKeyStatus) || {}),
  },
  notifications: {
    ...defaultUserSettings.notifications,
    ...((data && data.notifications) || {}),
  },
});

export const loadUserSettings = async (userId) => {
  if (!userId) return mergeUserSettings();
  try {
    const payload = await apiFetch("/api/user-settings");
    return mergeUserSettings(payload?.data || {});
  } catch (error) {
    return mergeUserSettings();
  }
};

export const saveUserSettings = async (userId, data) => {
  if (!userId) return;
  await apiFetch("/api/user-settings", {
    method: "PUT",
    body: { data },
  });
};

export const saveUserApiKeys = async ({ data = {}, clear = [] } = {}) =>
  apiFetch("/api/user-settings", {
    method: "PUT",
    body: {
      api_keys: data,
      api_key_clear: clear,
    },
  });
